import React, { useState } from 'react';
import { Card, SubjectType } from '../../types/Card';

interface CardEditorProps {
  card: Card;
  onSave: (updates: Partial<Card>) => void;
  onCancel: () => void;
}

const SUBJECT_CONFIG: Record<SubjectType, { color: string; emoji: string; label: string }> = {
  english:   { color: '#2E86AB', emoji: '📚', label: 'English' },
  maths:     { color: '#DC143C', emoji: '🔢', label: 'Mathematics' },
  science:   { color: '#4A90E2', emoji: '🔬', label: 'Science' },
  history:   { color: '#8B4513', emoji: '🏛️', label: 'History' },
  geography: { color: '#228B22', emoji: '🌍', label: 'Geography' },
  computing: { color: '#E4A101', emoji: '💻', label: 'Computing' },
  art:       { color: '#FF8C00', emoji: '🎨', label: 'Art & Design' },
  music:     { color: '#9C27B0', emoji: '🎵', label: 'Music' },
  pe:        { color: '#E91E63', emoji: '⚽', label: 'P.E.' },
  dt:        { color: '#795548', emoji: '🔧', label: 'Design & Technology' },
  re:        { color: '#607D8B', emoji: '🕊️', label: 'R.E.' },
  pshe:      { color: '#00BCD4', emoji: '💬', label: 'PSHE' },
  mfl:       { color: '#3F51B5', emoji: '🌐', label: 'Modern Languages' },
  space:     { color: '#9370DB', emoji: '🚀', label: 'Space' },
};

export const CardEditor: React.FC<CardEditorProps> = ({ card, onSave, onCancel }) => {
  const first = (v: string | string[]) => Array.isArray(v) ? v[0] || '' : v;

  const [editedCard, setEditedCard] = useState({
    topic: card.topic,
    prompts: {
      question: first(card.prompts.question),
      wonder: first(card.prompts.wonder),
      investigate: first(card.prompts.investigate),
    },
  });

  const [hasChanges, setHasChanges] = useState(false);

  const handleChange = (field: 'topic' | 'question' | 'wonder' | 'investigate', value: string) => {
    setHasChanges(true);
    
    if (field === 'topic') {
      setEditedCard((prev) => ({ ...prev, topic: value }));
    } else {
      setEditedCard((prev) => ({
        ...prev,
        prompts: {
          ...prev.prompts,
          [field]: value,
        },
      }));
    }
  };

  const handleSave = () => {
    if (!editedCard.topic.trim()) {
      alert('Topic name cannot be empty');
      return;
    }

    if (!editedCard.prompts.question.trim() || 
        !editedCard.prompts.wonder.trim() || 
        !editedCard.prompts.investigate.trim()) {
      alert('All prompts must have content');
      return;
    }

    onSave({
      topic: editedCard.topic.trim(),
      prompts: {
        question: editedCard.prompts.question.trim(),
        wonder: editedCard.prompts.wonder.trim(),
        investigate: editedCard.prompts.investigate.trim(),
      },
    });
  };

  const handleCancel = () => {
    if (hasChanges) {
      if (window.confirm('You have unsaved changes. Are you sure you want to cancel?')) {
        onCancel();
      }
    } else {
      onCancel();
    }
  };

  const subjectConfig = SUBJECT_CONFIG[card.subject];

  return (
    <div className="card-editor-overlay">
      <div className="card-editor-modal">
        <div className="card-editor-header">
          <div className="card-editor-subject">
            <span className="card-editor-emoji">{subjectConfig.emoji}</span>
            <span 
              className="card-editor-subject-label"
              style={{ color: subjectConfig.color }}
            >
              {subjectConfig.label}
            </span>
          </div>
          <button
            onClick={handleCancel}
            className="card-editor-close"
            aria-label="Close editor"
          >
            ✕
          </button>
        </div>

        <div className="card-editor-content">
          <div className="editor-field">
            <label htmlFor="topic-input" className="editor-label">
              Topic Name
            </label>
            <input
              id="topic-input"
              type="text"
              value={editedCard.topic}
              onChange={(e) => handleChange('topic', e.target.value)}
              className="editor-input"
              maxLength={100}
            />
          </div>

          <div className="editor-field">
            <label htmlFor="question-input" className="editor-label">
              Question Generator
            </label>
            <textarea
              id="question-input"
              value={editedCard.prompts.question}
              onChange={(e) => handleChange('question', e.target.value)}
              className="editor-textarea"
              rows={3}
              maxLength={500}
            />
            <div className="character-count">
              {editedCard.prompts.question.length} / 500
            </div>
          </div>

          <div className="editor-field">
            <label htmlFor="wonder-input" className="editor-label">
              Wondering
            </label>
            <textarea
              id="wonder-input"
              value={editedCard.prompts.wonder}
              onChange={(e) => handleChange('wonder', e.target.value)}
              className="editor-textarea"
              rows={3}
              maxLength={500}
            />
            <div className="character-count">
              {editedCard.prompts.wonder.length} / 500
            </div>
          </div>

          <div className="editor-field">
            <label htmlFor="investigate-input" className="editor-label">
              Investigation
            </label>
            <textarea
              id="investigate-input"
              value={editedCard.prompts.investigate}
              onChange={(e) => handleChange('investigate', e.target.value)}
              className="editor-textarea"
              rows={3}
              maxLength={500}
            />
            <div className="character-count">
              {editedCard.prompts.investigate.length} / 500
            </div>
          </div>
        </div>

        <div className="card-editor-footer">
          <button
            onClick={handleCancel}
            className="editor-button editor-button-cancel"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="editor-button editor-button-save"
            disabled={!hasChanges}
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};
